import { gql } from '@apollo/client';

export const FORMAT_TEMPLATE_FIELDS = gql`
  fragment FormatTemplateFields on FormatTemplate {
    _id
    name
    scope
    source
    sport
    companyId
    ageGroupBuckets
    minPlayers
    maxPlayers
    imageUrl
    totalAssessmentSessions
    teamOutcomes
    stageBlueprints
    bands {
      key
      label
      minPlayers
      maxPlayers
      groupCount
      teamOutcomes
      totalAssessmentSessions
      imageUrl
    }
  }
`;

export const GET_FORMAT_TEMPLATES = gql`
  query GetFormatTemplates($companyId: ID) {
    formatTemplates(companyId: $companyId) {
      ...FormatTemplateFields
    }
  }

  ${FORMAT_TEMPLATE_FIELDS}
`;

// stageLibrary is nested (no per-definition id), so the cache replaces the array wholesale on
// every write — the read query and the catalog update must select it identically via this fragment.
export const STAGE_LIBRARY_FIELDS = gql`
  fragment StageLibraryFields on FormatTemplate {
    stageLibrary {
      key
      type
      name
      title
      sessionType
      direction
      config
      bandKeys
    }
  }
`;

// Catalog-editor read: one template's stage configs, which are heavy (drill lists per
// definition) — they load here, on open, instead of riding FormatTemplateFields everywhere.
export const GET_FORMAT_TEMPLATE_STAGE_LIBRARY = gql`
  query GetFormatTemplateStageLibrary($_id: ID!, $companyId: ID) {
    formatTemplate(_id: $_id, companyId: $companyId) {
      _id
      ...StageLibraryFields
    }
  }

  ${STAGE_LIBRARY_FIELDS}
`;

// Resolves the stages an apply would insert, band already selected from the age group's live
// roster. The only way to read a CATALOG template's stages — it has no stageBlueprints.
export const FORMAT_TEMPLATE_PREVIEW = gql`
  query GetFormatTemplatePreview($templateId: ID!, $ageGroupId: ID!, $bandKey: String) {
    formatTemplatePreview(templateId: $templateId, ageGroupId: $ageGroupId, bandKey: $bandKey) {
      templateId
      bandKey
      goalieCount
      nonGoalieCount
      totalAssessmentSessions
      stages {
        type
        order
        title
        config
      }
    }
  }
`;

export const CREATE_FORMAT_TEMPLATE = gql`
  mutation CreateFormatTemplate($data: CreateFormatTemplateInput!) {
    createFormatTemplate(data: $data) {
      ...FormatTemplateFields
    }
  }

  ${FORMAT_TEMPLATE_FIELDS}
`;

export const UPDATE_FORMAT_TEMPLATE = gql`
  mutation UpdateFormatTemplate($_id: ID!, $data: UpdateFormatTemplateInput!) {
    updateFormatTemplate(_id: $_id, data: $data) {
      _id
      name
      ageGroupBuckets
      minPlayers
      maxPlayers
      teamOutcomes
      stageBlueprints
    }
  }
`;

// The catalog variant of updateFormatTemplate: settings edits change stageLibrary, so that is
// what it selects back; band diagram changes ride refetchQueries (bands are signed-URL derived).
export const UPDATE_FORMAT_TEMPLATE_CATALOG = gql`
  mutation UpdateFormatTemplateCatalog($_id: ID!, $data: UpdateFormatTemplateInput!) {
    updateFormatTemplate(_id: $_id, data: $data) {
      _id
      ...StageLibraryFields
    }
  }

  ${STAGE_LIBRARY_FIELDS}
`;

// Mints a signed PUT only — nothing persists until the returned imagePath is sent back through
// updateFormatTemplate, so an abandoned upload never dangles from a template.
export const ISSUE_FORMAT_TEMPLATE_DIAGRAM_UPLOAD = gql`
  mutation IssueFormatTemplateDiagramUpload($_id: ID!, $contentType: String!, $bandKey: String) {
    issueFormatTemplateDiagramUpload(_id: $_id, contentType: $contentType, bandKey: $bandKey) {
      uploadUrl
      imagePath
      contentType
    }
  }
`;

export const DELETE_FORMAT_TEMPLATE = gql`
  mutation DeleteFormatTemplate($_id: ID!) {
    deleteFormatTemplate(_id: $_id) {
      _id
    }
  }
`;

// Promotion clears companyId (the unique index then enforces the name globally) and demotion
// restores it, so both are the fields this mutation changes.
export const SET_FORMAT_TEMPLATE_GLOBAL = gql`
  mutation SetFormatTemplateGlobal($_id: ID!, $value: Boolean!) {
    setFormatTemplateGlobal(_id: $_id, value: $value) {
      _id
      scope
      companyId
    }
  }
`;

export const APPLY_FORMAT_TEMPLATE = gql`
  mutation ApplyFormatTemplate(
    $_id: ID!
    $templateId: ID!
    $ageGroupId: ID!
    $mode: ApplyTemplateMode!
    $bandKey: String
  ) {
    applyFormatTemplate(
      _id: $_id
      templateId: $templateId
      ageGroupId: $ageGroupId
      mode: $mode
      bandKey: $bandKey
    ) {
      _id
    }
  }
`;
